/**
 * Utilitários de validação e verificação de tipos
 */

import { Paciente } from './validators';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Type guards para verificação de tipos em runtime
 */
export class TypeGuards {
  /**
   * Verifica se um valor é uma string não vazia
   */
  static isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  /**
   * Verifica se um valor é uma data válida
   */
  static isValidDate(value: unknown): boolean {
    if (!value) return false;
    const date = new Date(value as string | number | Date);
    return !isNaN(date.getTime());
  }

  /**
   * Verifica se um valor é um número válido
   */
  static isValidNumber(value: unknown): boolean {
    return typeof value === 'number' && !isNaN(value) && isFinite(value);
  }
}

/**
 * Utilitários para validação de dados
 */
export class DataValidators {
  static validatePacientes(data: any): any[] {

    if (!data) {

      return [];
    }

    if (!Array.isArray(data)) {

      return [];
    }

    const pacientesValidados = data.filter(paciente => {
      // Validação básica dos campos obrigatórios
      if (!paciente || typeof paciente !== 'object') {

        return false;
      }

      // Verificar se possui ID válido
      if (!paciente.id || (typeof paciente.id !== 'string' && typeof paciente.id !== 'number')) {

        return false;
      }

      // Verificar se possui nome
      if (!paciente.nome || typeof paciente.nome !== 'string' || paciente.nome.trim() === '') {

        return false;
      }

      // Normalizar campo ativo (pode vir como 0/1, true/false, ou string)
      if (paciente.ativo !== undefined) {
        paciente.ativo = Boolean(paciente.ativo && paciente.ativo !== '0' && paciente.ativo !== 0);
      } else {
        paciente.ativo = true; // Padrão para ativo se não especificado
      }

      // Normalizar campos de string para evitar null/undefined
      paciente.telefone = paciente.telefone || '';
      paciente.email = paciente.email || '';
      paciente.cpf = paciente.cpf || '';
      paciente.servico = paciente.servico || 'psicoterapia';

      return true;
    });

    return pacientesValidados;
  }

  static validateConsultas(data: any): any[] {

    if (!data) {

      return [];
    }

    if (!Array.isArray(data)) {

      return [];
    }

    const consultasValidadas = data.filter(consulta => {
      // Validação básica dos campos obrigatórios
      if (!consulta || typeof consulta !== 'object') {

        return false;
      }

      // Verificar se possui ID válido
      if (!consulta.id || (typeof consulta.id !== 'string' && typeof consulta.id !== 'number')) {

        return false;
      }

      // Verificar campos obrigatórios
      if (!consulta.pacienteId && !consulta.paciente_id) {

        return false;
      }

      // Normalizar IDs para string
      consulta.id = String(consulta.id);
      consulta.pacienteId = String(consulta.pacienteId || consulta.paciente_id);
      consulta.usuarioId = String(consulta.usuarioId || consulta.usuario_id || '1');

      // Normalizar campos de data/hora
      consulta.data = consulta.dataConsulta || consulta.data || '';
      consulta.hora = consulta.horaInicio || consulta.hora || '';
      consulta.dataConsulta = consulta.dataConsulta || consulta.data || '';
      consulta.horaInicio = consulta.horaInicio || consulta.hora || '';

      // Normalizar valor numérico
      consulta.valor = Number(consulta.valor) || 0;

      // Normalizar status
      consulta.statusConsulta = consulta.statusConsulta || (consulta.realizada ? 'realizada' : 'agendada');
      consulta.statusPagamento = consulta.statusPagamento || (consulta.pagamentoRecebido ? 'pago' : 'pendente');

      // Campos computed para compatibilidade
      consulta.realizada = consulta.statusConsulta === 'realizada';
      consulta.pagamentoRecebido = consulta.statusPagamento === 'pago';

      return true;
    });

    // CORREÇÃO CRÍTICA: Adicionar o return que estava faltando
    return consultasValidadas;
  }
  
  // CORREÇÃO: Adicionar métodos auxiliares que estão sendo usados mas não existem
  static safeString(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value);
  }
  
  static safeDate(value: any): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value;
    }
    return undefined;
  }
  
  static safeNumber(value: any): number {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
}

// Instância para compatibilidade com código existente
const validators = new DataValidators();

// Exports para compatibilidade
export const validatePacientes = DataValidators.validatePacientes;
export const validateConsultas = DataValidators.validateConsultas;
export { validators };

// Re-exportar para manter compatibilidade
export type { Paciente } from '../services/api/pacientesService';
